import styled from '@emotion/styled';
import { RefreshCw, ChevronRight } from 'lucide-react';
import type { CustomerSubscription, SubscriptionStatus } from '@/domain/entities/subscription.entity';
import { useProductImageUrl } from '@/lib/hooks/use-product';

const STATUS_CONFIG: Record<SubscriptionStatus, { color: string; label: string }> = {
  active: { color: '#2F806A', label: 'Active' },
  paused: { color: '#B8860B', label: 'Paused' },
  pending: { color: '#5A5A5A', label: 'Pending' },
  failed: { color: '#C53030', label: 'Failed' },
  suspended: { color: '#C53030', label: 'Suspended' },
  cancelled: { color: '#5A5A5A', label: 'Cancelled' },
};

function getDaysUntilNextDelivery(nextChargeDate: string | null): number | null {
  if (!nextChargeDate) return null;
  const now = new Date();
  const next = new Date(nextChargeDate);
  const diffMs = next.getTime() - now.getTime();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatFrequencyLabel(frequencyDays: number): string {
  if (frequencyDays <= 31) return 'Deliver every month';
  if (frequencyDays <= 62) return 'Deliver every 2 months';
  if (frequencyDays <= 93) return 'Deliver every 3 months';
  if (frequencyDays <= 124) return 'Deliver every 4 months';
  if (frequencyDays <= 186) return 'Deliver every 6 months';
  return 'Deliver every year';
}

const CardContainer = styled.div`
  background: #f7f7f7;
  border-radius: 8px;
  display: flex;
  overflow: hidden;
  min-height: 140px;
`;

const ProductImage = styled.div`
  width: 120px;
  min-height: 100%;
  flex-shrink: 0;
  background: #f0f0f0;
  border-radius: 8px 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #767676;
  font-size: 12px;
  overflow: hidden;
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProductName = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #292929;
`;

const FrequencyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #292929;
`;

const FrequencyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #292929;
`;

const FrequencyText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.33;
  color: #292929;
`;

const DeliveryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DeliveryText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #292929;
`;

const DeliveryChevron = styled.span`
  display: inline-flex;
  align-items: center;
  color: #292929;
  flex-shrink: 0;
`;

const ManageButton = styled.button`
  background: #fff;
  border: 1px solid #d1d1d1;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #292929;
  cursor: pointer;
  text-align: center;
  transition: background 0.15s;

  &:hover {
    background: #f0f0f0;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
`;

const StatusDot = styled.div<{ dotColor: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ dotColor }) => dotColor};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '\u2713';
    color: #fff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
  }
`;

const StatusLabel = styled.span<{ labelColor: string }>`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.33;
  color: ${({ labelColor }) => labelColor};
`;

interface SubscriptionCardProps {
  readonly subscription: CustomerSubscription;
  readonly onManage?: (subscription: CustomerSubscription) => void;
}

export function SubscriptionCard({ subscription, onManage }: SubscriptionCardProps) {
  const statusConfig = STATUS_CONFIG[subscription.status] ?? STATUS_CONFIG.pending;
  const isActionable = subscription.status === 'active' || subscription.status === 'paused';

  const firstItem = subscription.items?.[0];
  const productName = firstItem?.name ?? subscription.plan.name;
  const imageUrl = useProductImageUrl(firstItem?.product_id, firstItem?.image_url);

  const matchedFrequency = subscription.plan.frequencies?.find(
    (f) => f.id === subscription.plan_frequency_id,
  );
  const frequencyDays = matchedFrequency?.frequency_days ?? 30;

  const daysUntil = getDaysUntilNextDelivery(subscription.next_charge_date);

  return (
    <CardContainer>
      <ProductImage>
        {imageUrl ? (
          <ProductImg src={imageUrl} alt={productName} />
        ) : (
          <RefreshCw size={24} />
        )}
      </ProductImage>

      <CardBody>
        <TopRow>
          <ProductName>{productName}</ProductName>
          <FrequencyRow>
            <FrequencyIcon>
              <RefreshCw size={14} />
            </FrequencyIcon>
            <FrequencyText>{formatFrequencyLabel(frequencyDays)}</FrequencyText>
          </FrequencyRow>
        </TopRow>

        {daysUntil !== null && (
          <DeliveryRow>
            <DeliveryText>
              {daysUntil === 0
                ? 'Delivery today'
                : `${daysUntil} days until next delivery`}
            </DeliveryText>
            <DeliveryChevron>
              <ChevronRight size={16} />
            </DeliveryChevron>
          </DeliveryRow>
        )}

        {!isActionable && (
          <StatusBadge>
            <StatusDot dotColor={statusConfig.color} />
            <StatusLabel labelColor={statusConfig.color}>
              {statusConfig.label}
            </StatusLabel>
          </StatusBadge>
        )}

        {isActionable && onManage && (
          <ManageButton
            type="button"
            onClick={() => onManage(subscription)}
          >
            Manage subscription
          </ManageButton>
        )}
      </CardBody>
    </CardContainer>
  );
}
